import { Nullable } from '@rbi-ctg/frontend';
import { IRestaurant } from '@rbi-ctg/store';
import { OrderingStatus } from 'generated/graphql-gateway';
import { ServiceMode } from 'state/service-mode/types';
import LocalStorage, { StorageKeys } from 'utils/local-storage';

import { STATE_MAP } from '../constants';
import {
  IAvailabilityProps,
  IMergeRestaurantData,
  IPreloadedStoreState,
  StorePermissions,
  curriedGetStoreStatus,
} from '../types';

export * from './store-time-utils';
export * from './store-geo-spatial-utils';

export const getUSState = (stateProvince: string): string =>
  STATE_MAP[stateProvince] ?? stateProvince;

export const remapStore = <S>(s: S) => s;

/**
 * Creates a function to get a store's status for store selection 1.0
 * TODO: This should be either deprecated in favor of `isRestaurantAvailable` in
 * use-service-mode-status/index.ts or added as an exported member of that hook as
 * an alternative to `isRestaurantAvailable`.
 */
export const curriedGetStoreStatusV1: curriedGetStoreStatus = ({
  store,
  serviceModeStatuses,
  selectedServiceMode,
}) => (serviceMode = selectedServiceMode) => {
  const noStoreSelected =
    !store ||
    (store &&
      'selectedUnavailableStoreNumber' in store &&
      store.selectedUnavailableStoreNumber === null);

  if (noStoreSelected || !serviceMode) {
    return StorePermissions.NO_STORE_SELECTED;
  }

  const serviceModeStatus = serviceModeStatuses.find(sms => sms.serviceMode === serviceMode);
  const unavailableStoreSelected = store && !('hasMobileOrdering' in store);

  if (unavailableStoreSelected) {
    return StorePermissions.OPEN_AND_UNAVAILABLE;
  }

  return serviceModeStatus?.available && !serviceModeStatus.featureFlagDisabled
    ? StorePermissions.OPEN_AND_AVAILABLE
    : StorePermissions.CLOSED;
};

export const preloadedStoreInfo = (): IPreloadedStoreState =>
  LocalStorage.getItem(StorageKeys.STORE) || ({} as IPreloadedStoreState);

export const isAvailable = (rbiRestaurant?: IAvailabilityProps): boolean =>
  rbiRestaurant?.isAvailable ?? rbiRestaurant?.available ?? true;

/**
 * Merges the data from the GetRestaurant query and sanity query
 *
 * @param {IMergeRestaurantData} data
 * * rbiRestaurant - The restaurant returned from GetRestaurant
 * * sanityStore - the sanity document queried from groq
 *
 * @returns {IStore} merged store document
 *
 *
 * *NOTE* at the moment only the operating hours and timezone come from GetRestaurant
 */
export const mergeRestaurantData = ({
  rbiRestaurant,
  sanityStore,
}: IMergeRestaurantData): IRestaurant => {
  return {
    ...sanityStore,
    available: rbiRestaurant.available,
    curbsideHours: rbiRestaurant.curbsideHours || sanityStore.curbsideHours || null,
    deliveryHours: rbiRestaurant.deliveryHours || sanityStore.deliveryHours || null,
    diningRoomHours: rbiRestaurant.diningRoomHours || sanityStore.diningRoomHours || null,
    driveThruHours: rbiRestaurant.driveThruHours || sanityStore.driveThruHours || null,
    mobileOrderDriveThruHours:
      rbiRestaurant.mobileOrderDriveThruHours || sanityStore.mobileOrderDriveThruHours || null,
    timezone: rbiRestaurant.timezone,
    localTime: rbiRestaurant.localTime,
    serviceModeHours: rbiRestaurant.serviceModeHours,
    dayPartHours: rbiRestaurant.dayPartHours,
  };
};

/**
 * Helper function to initialize store for store selection 1.0
 *
 * @param {IPreloadedStoreState} preloaded Preloaded store
 * @param {Nullable<ServiceMode>} serviceMode Selected service mode
 */
export const initStore = (
  preloaded: IPreloadedStoreState,
  serviceMode: Nullable<ServiceMode>
): IRestaurant | null => (!!preloaded.store && serviceMode ? preloaded.store : null);

/*
 * isMobileOrderingAvailable compares the app environment with the
 * restaurant environment returned from Sanity
 */

export const isMobileOrderingAvailable = (
  restaurant: IRestaurant,
  validMobileOrderingEnvs: string[] = []
) => {
  const { mobileOrderingStatus } = restaurant;

  if (!restaurant?.hasMobileOrdering || !mobileOrderingStatus) {
    return false;
  }
  if ([OrderingStatus.ALPHA, OrderingStatus.BETA].includes(mobileOrderingStatus)) {
    return false;
  }

  return validMobileOrderingEnvs.includes(mobileOrderingStatus);
};

export const isMobileOrderingAvailableExperiment = (
  restaurant: Pick<IRestaurant, 'mobileOrderingStatus' | 'hasMobileOrdering'>,
  validMobileOrderingEnvs: string[] = []
): boolean => {
  const { mobileOrderingStatus, hasMobileOrdering } = restaurant;

  if (!hasMobileOrdering || !mobileOrderingStatus) {
    return false;
  }
  if ([OrderingStatus.ALPHA, OrderingStatus.BETA].includes(mobileOrderingStatus)) {
    return false;
  }

  return validMobileOrderingEnvs.includes(mobileOrderingStatus);
};
